// MARK://dhk8/admin-shell/pages/modules/ReservationDetailPage
// Ficha "ver-primero" de una reserva (Gustavo 2026-06-23). Header con nombre +
// fecha/hora + pills (estado + código + ocasión/origen) + KPIs (personas, mesa,
// duración) + Tabs (General, Historial). Entidad transaccional: ficha mínima
// (General read-only + Editar sheet + Historial). El tab General se ve primero;
// "Editar" abre el MISMO formulario que el modal de ReservationsPage (REUBICADO,
// mismos campos). Acciones rápidas de estado (Llegó/Sentada/No-show/Cancelar) en el
// header, igual que la fila de la lista.
//
// Carga por id: NO existe resv.getReservation(id), así que traemos un rango amplio
// con resv.listReservations({from,to}) y hacemos find(id) (mismo patrón que
// ProductDetailPage usa con las recetas). EDITAR usa resv.updateReservation(id, ...).
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ChevronLeft, Pencil, Baby, Accessibility, AlertTriangle,
  LogIn, Armchair, UserX, Trash2,
} from 'lucide-react'

import * as resv from '../../lib/clients/reservations.js'
import * as floor from '../../lib/clients/floor.js'
import type { FloorTable } from '../../lib/mock-data.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import StatusPill, { type StatusTone } from '../../components/StatusPill.js'
import { SummaryStrip } from '../../components/StatCard.js'
import Tabs from '../../components/Tabs.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import AuditTrail from '../../components/AuditTrail.js'
import { useToast } from '../../components/Toast.js'

type Tab = 'general' | 'historial'

const STATUS_TONE: Record<resv.ReservationDto['status'], StatusTone> = {
  CONFIRMED: 'info',
  ARRIVED: 'warn',
  SEATED: 'ok',
  CANCELLED: 'neutral',
  NO_SHOW: 'danger',
}
function statusLabel(s: resv.ReservationDto['status']): string {
  switch (s) {
    case 'CONFIRMED': return 'Confirmada'
    case 'ARRIVED': return 'Llegó'
    case 'SEATED': return 'Sentada'
    case 'CANCELLED': return 'Cancelada'
    case 'NO_SHOW': return 'No-show'
  }
}

const OCCASION_LABEL: Record<NonNullable<resv.ReservationDto['occasion']>, string> = {
  birthday: '🎂 Cumpleaños',
  anniversary: '💍 Aniversario',
  date: '❤️ Cita',
  business: '💼 Negocios',
  other: 'Otro',
}
const SOURCE_LABEL: Record<NonNullable<resv.ReservationDto['source']>, string> = {
  walk_in: 'Walk-in',
  phone: 'Llamada',
  web: 'Web',
  instagram: 'Instagram',
  recommended: 'Recomendado',
  other: 'Otro',
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es-MX', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}
function isoDatePart(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function isoTimePart(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

interface EditForm {
  name: string
  phone: string
  email: string
  partySize: number
  date: string
  time: string
  durationMin: number
  tableId: string
  highChair: boolean
  wheelchair: boolean
  occasion: '' | NonNullable<resv.ReservationDto['occasion']>
  source: '' | NonNullable<resv.ReservationDto['source']>
  allergies: string
  notes: string
}

export default function ReservationDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  // No hay resv.getReservation(id): traemos un rango amplio y hacemos find(id).
  const list = useAsync((s) => resv.listReservations({ from: '2000-01-01T00:00:00.000Z', to: '2100-01-01T00:00:00.000Z' }, s), [id])
  const layout = useAsync((s) => floor.getLayout(s))
  const tables = useMemo<FloorTable[]>(() => layout.data?.tables ?? [], [layout.data])

  const [tab, setTab] = useState<Tab>('general')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<EditForm | null>(null)
  const [busy, setBusy] = useState(false)
  const [rowBusy, setRowBusy] = useState(false)

  const r = useMemo(() => (list.data ?? []).find((x) => x.id === id) ?? null, [list.data, id])
  const tableLabel = (tid: string | null): string => {
    if (!tid) return '—'
    const t = tables.find((x) => x.id === tid)
    return t ? `Mesa ${t.code}` : '—'
  }

  function openEdit() {
    if (!r) return
    setForm({
      name: r.name,
      phone: r.phone ?? '',
      email: r.email ?? '',
      partySize: r.partySize,
      date: isoDatePart(r.reservedAt),
      time: isoTimePart(r.reservedAt),
      durationMin: r.durationMin ?? 90,
      tableId: r.tableId ?? '',
      highChair: r.highChair,
      wheelchair: r.wheelchair,
      occasion: r.occasion ?? '',
      source: r.source ?? '',
      allergies: r.allergies ?? '',
      notes: r.notes ?? '',
    })
    setEditing(true)
  }

  async function save() {
    if (!form || !r) return
    if (!form.name.trim()) { toast('Nombre requerido', 'danger'); return }
    setBusy(true)
    try {
      const reservedAt = new Date(`${form.date}T${form.time}:00`).toISOString()
      await resv.updateReservation(r.id, {
        name: form.name.trim(),
        partySize: form.partySize,
        reservedAt,
        durationMin: form.durationMin,
        highChair: form.highChair,
        wheelchair: form.wheelchair,
        tableId: form.tableId || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        notes: form.notes.trim() || null,
        allergies: form.allergies.trim() || null,
        occasion: form.occasion || null,
        source: form.source || null,
      })
      toast('Reserva actualizada', 'success'); setEditing(false); list.reload()
    } catch (e) { toast(e instanceof Error ? e.message : 'No se pudo guardar', 'danger') } finally { setBusy(false) }
  }

  async function setStatus(status: resv.ReservationDto['status']) {
    if (!r) return
    setRowBusy(true)
    try { await resv.updateReservation(r.id, { status }); toast('Estado actualizado', 'success'); list.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo actualizar el estado.', 'danger') }
    finally { setRowBusy(false) }
  }
  async function handleCancel() {
    if (!r) return
    if (!confirm(`¿Cancelar reserva de ${r.name}?`)) return
    setRowBusy(true)
    try { await resv.cancelReservation(r.id); toast('Reserva cancelada', 'success'); list.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo cancelar.', 'danger') }
    finally { setRowBusy(false) }
  }

  const kpis = useMemo(() => {
    if (!r) return []
    return [
      { label: 'Personas', value: String(r.partySize) },
      { label: 'Mesa', value: tableLabel(r.tableId) },
      { label: 'Duración', value: r.durationMin ? `${r.durationMin} min` : '—' },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r, tables])

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/reservations')} aria-label="Volver a reservas"><ChevronLeft size={18} /></button>
          <div><h1 className="adm-page__title">Reserva</h1><p className="adm-page__sub">Reservas · ficha de la reserva</p></div>
        </div>
      </div>

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && !r && (
        <div className="adm-empty">No se encontró esta reserva. Puede haber sido eliminada.</div>
      )}

      {r && (
        <EntityDetailPage
          title={r.name}
          subtitle={fmtDateTime(r.reservedAt)}
          pills={<>
            <StatusPill tone={STATUS_TONE[r.status]} label={statusLabel(r.status)} />
            {r.reservationCode && <StatusPill tone="neutral" label={r.reservationCode} />}
            {r.occasion && <StatusPill tone="info" label={OCCASION_LABEL[r.occasion]} />}
          </>}
          kpis={kpis.length > 0 ? <SummaryStrip items={kpis} /> : undefined}
          actions={<>
            {r.status !== 'CANCELLED' && (
              <>
                {r.status !== 'ARRIVED' && r.status !== 'SEATED' && (
                  <button className="adm-btn adm-btn--sm" disabled={rowBusy} onClick={() => void setStatus('ARRIVED')} title="Marcar como llegó"><LogIn size={14} /> Llegó</button>
                )}
                {r.status !== 'SEATED' && (
                  <button className="adm-btn adm-btn--sm" disabled={rowBusy} onClick={() => void setStatus('SEATED')} title="Marcar como sentada"><Armchair size={14} /> Sentada</button>
                )}
                {r.status !== 'SEATED' && r.status !== 'NO_SHOW' && (
                  <button className="adm-btn adm-btn--sm" disabled={rowBusy} onClick={() => void setStatus('NO_SHOW')} title="Marcar como no-show"><UserX size={14} /> No-show</button>
                )}
                <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openEdit}><Pencil size={15} /> Editar</button>
                {r.status !== 'SEATED' && (
                  <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" disabled={rowBusy} onClick={() => void handleCancel()} title="Cancelar reserva"><Trash2 size={15} /></button>
                )}
              </>
            )}
          </>}
          tabs={<Tabs
            tabs={[
              { id: 'general', label: 'General' },
              { id: 'historial', label: 'Historial' },
            ]}
            active={tab}
            onChange={(t) => setTab(t as Tab)}
            ariaLabel="Secciones de la reserva"
          />}
        >
          {tab === 'general' && (
            <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
              <Field label="Cliente">{r.name}</Field>
              <Field label="Código"><span className="adm-mono">{r.reservationCode || '—'}</span></Field>
              <Field label="Fecha y hora">{fmtDateTime(r.reservedAt)}</Field>
              <Field label="Duración">{r.durationMin ? `${r.durationMin} min` : <span className="adm-muted">—</span>}</Field>
              <Field label="Personas">{r.partySize}</Field>
              <Field label="Mesa">{tableLabel(r.tableId)}</Field>
              <Field label="Teléfono">{r.phone ? <a href={`tel:${r.phone}`} style={{ color: 'inherit' }}>{r.phone}</a> : <span className="adm-muted">—</span>}</Field>
              <Field label="Email">{r.email ? <a href={`mailto:${r.email}`} style={{ color: 'inherit' }}>{r.email}</a> : <span className="adm-muted">—</span>}</Field>
              <Field label="Ocasión">{r.occasion ? OCCASION_LABEL[r.occasion] : <span className="adm-muted">—</span>}</Field>
              <Field label="Origen">{r.source ? SOURCE_LABEL[r.source] : <span className="adm-muted">—</span>}</Field>
              <Field label="Requerimientos">
                <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
                  {r.highChair && <span className="adm-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><Baby size={12} /> Silla bebé</span>}
                  {r.wheelchair && <span className="adm-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><Accessibility size={12} /> Accesible</span>}
                  {!r.highChair && !r.wheelchair && <span className="adm-muted">—</span>}
                </span>
              </Field>
              <Field label="Alergias / restricciones">
                {r.allergies
                  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--pos-warning)' }}><AlertTriangle size={13} /> {r.allergies}</span>
                  : <span className="adm-muted">—</span>}
              </Field>
              <Field label="Notas">{r.notes || <span className="adm-muted">—</span>}</Field>
              <Field label="Estado"><StatusPill tone={STATUS_TONE[r.status]} label={statusLabel(r.status)} /></Field>
            </div>
          )}

          {tab === 'historial' && <AuditTrail entityId={r.id} />}
        </EntityDetailPage>
      )}

      {form && (
        <SectionedSheet
          open={editing}
          title="Editar reserva"
          onClose={() => setEditing(false)}
          footer={<>
            <button className="adm-btn" onClick={() => setEditing(false)} disabled={busy}>Cancelar</button>
            <button className="adm-btn adm-btn--primary" onClick={save} disabled={busy || !form.name.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button>
          </>}
        >
          <FormSection title="Cliente">
            <label className="adm-field"><span className="adm-field__label">Nombre</span><input className="adm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus /></label>
            <label className="adm-field"><span className="adm-field__label">Teléfono</span><input className="adm-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+52..." /></label>
            <label className="adm-field"><span className="adm-field__label">Email</span><input className="adm-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="cliente@..." /></label>
          </FormSection>

          <FormSection title="Fecha y mesa">
            <label className="adm-field"><span className="adm-field__label">Fecha</span><input className="adm-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></label>
            <label className="adm-field"><span className="adm-field__label">Hora</span><input className="adm-input" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></label>
            <label className="adm-field"><span className="adm-field__label">Duración (min)</span><input className="adm-input" type="number" min={15} max={600} step={15} value={form.durationMin} onChange={(e) => setForm({ ...form, durationMin: Math.max(15, Number(e.target.value) || 90) })} /></label>
            <label className="adm-field"><span className="adm-field__label">Personas</span><input className="adm-input" type="number" min={1} max={50} value={form.partySize} onChange={(e) => setForm({ ...form, partySize: Math.max(1, Number(e.target.value) || 1) })} /></label>
            <label className="adm-field"><span className="adm-field__label">Mesa</span>
              <select className="adm-select" value={form.tableId} onChange={(e) => setForm({ ...form, tableId: e.target.value })} disabled={tables.length === 0}>
                <option value="">— Sin asignar —</option>
                {tables.map((t) => <option key={t.id} value={t.id}>Mesa {t.code} ({t.capacity} pers.)</option>)}
              </select>
            </label>
          </FormSection>

          <FormSection title="Detalles">
            <label className="adm-field"><span className="adm-field__label">Ocasión</span>
              <select className="adm-select" value={form.occasion} onChange={(e) => setForm({ ...form, occasion: e.target.value as EditForm['occasion'] })}>
                <option value="">—</option>
                <option value="birthday">Cumpleaños</option>
                <option value="anniversary">Aniversario</option>
                <option value="date">Cita</option>
                <option value="business">Negocios</option>
                <option value="other">Otro</option>
              </select>
            </label>
            <label className="adm-field"><span className="adm-field__label">Origen</span>
              <select className="adm-select" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value as EditForm['source'] })}>
                <option value="">—</option>
                <option value="walk_in">Walk-in</option>
                <option value="phone">Llamada</option>
                <option value="web">Web</option>
                <option value="instagram">Instagram</option>
                <option value="recommended">Recomendado</option>
                <option value="other">Otro</option>
              </select>
            </label>
            <div style={{ display: 'flex', gap: 16, padding: '4px 0' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}><input type="checkbox" checked={form.highChair} onChange={(e) => setForm({ ...form, highChair: e.target.checked })} /> <Baby size={14} /> Silla para bebé</label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}><input type="checkbox" checked={form.wheelchair} onChange={(e) => setForm({ ...form, wheelchair: e.target.checked })} /> <Accessibility size={14} /> Accesibilidad</label>
            </div>
            <label className="adm-field"><span className="adm-field__label">Alergias / restricciones</span><input className="adm-input" value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} placeholder="Maní, gluten, vegetariano..." /></label>
            <label className="adm-field"><span className="adm-field__label">Notas</span><input className="adm-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Mesa cerca ventana, sorpresa..." /></label>
          </FormSection>
        </SectionedSheet>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pos-fg-tertiary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--pos-fg)' }}>{children}</div>
    </div>
  )
}
